// src/pages/Appli/AppliAdmin/Prospects/ProspectsPage.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSync, FaColumns, FaTable } from "react-icons/fa";
import { IoPersonAddSharp } from "react-icons/io5";

import Button from "../../../../components/Button/Button";
import useScrollToTop from "../../../../hooks/useScrollToTop";

import {
  getAllProspects,
  addProspect,
  updateProspect,
  deleteProspect,
} from "../../../../services/prospectsServices";
import { getAllAssociations } from "../../../../services/associationsServices";
import { getAllProductsApp } from "../../../../services/productsappServices";

import { ProspectsFilters } from "./components/ProspectsFilters";
import { ProspectsTable } from "./components/ProspectsTable";
import { ProspectsCards } from "./components/ProspectsCards";
import { KanbanProspectsDnD } from "./components/KanbanProspectsDnD";
import { InlineProspectStatutSelect } from "./components/InlineProspectStatutSelect";

import AddProspectModal from "./modals/AddProspectModal";
import EditProspectModal from "./modals/EditProspectModal";
import { DuplicateProspects } from "../ProspectionPage/components/DuplicateProspects";

const GRADIENT =
  "bg-gradient-to-r from-[#5A8DEE] via-[#8753FF] to-[#FF6A88] text-white";

const STATUTS_ORDER = [
  "Nouveau Prospect",
  "A Recontacter",
  "Contacté & en attente",
  "Prospect Qualifié",
  "En Test",
  "Client",
  "Client SAAS",
  "Perdu",
];

export default function ProspectsPage() {
  useScrollToTop();

  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [products, setProducts] = useState([]);

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatut, setSelectedStatut] = useState("");
  const [selectedProvenance, setSelectedProvenance] = useState("");
  const [selectedAssociation, setSelectedAssociation] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [sortBy, setSortBy] = useState("statut"); // 'statut' | 'nom' | 'date'

  // Vue
  const [viewMode, setViewMode] = useState("auto"); // 'auto' | 'table' | 'cards' | 'kanban'

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [p, a, pr] = await Promise.all([
        getAllProspects(),
        getAllAssociations(),
        getAllProductsApp(),
      ]);
      setProspects(Array.isArray(p) ? p : []);
      setAssociations(Array.isArray(a) ? a : []);
      setProducts(Array.isArray(pr) ? pr : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des prospects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const provenanceOptions = useMemo(() => {
    const s = new Set();
    for (const p of prospects) if (p.provenance) s.add(p.provenance);
    return Array.from(s).sort((a, b) => a.localeCompare(b, "fr"));
  }, [prospects]);

  const associationOptions = useMemo(() => {
    const map = new Map();
    for (const a of associations) map.set(a.id, a.nom_asso);
    return Array.from(map.entries()).map(([id, nom]) => ({ id, nom }));
  }, [associations]);

  const productOptions = useMemo(() => {
    return products.map((p) => ({ id: p.id, nom: p.nom_app }));
  }, [products]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let out = prospects.filter((p) => {
      const assoName = p.association?.nom_asso || p.Association?.nom_asso || "";
      const prodName = p.interet_produit?.nom_app || "";

      const matchesTerm =
        !term ||
        `${p.nom || ""} ${p.prenom || ""}`.toLowerCase().includes(term) ||
        assoName.toLowerCase().includes(term) ||
        (p.fonction || "").toLowerCase().includes(term) ||
        (p.email || "").toLowerCase().includes(term) ||
        (p.telephone || "").toLowerCase().includes(term) ||
        prodName.toLowerCase().includes(term);

      const matchesStatut = selectedStatut
        ? (p.statut || "").toLowerCase() === selectedStatut.toLowerCase()
        : true;

      const matchesProv = selectedProvenance
        ? (p.provenance || "").toLowerCase() ===
          selectedProvenance.toLowerCase()
        : true;

      const matchesAsso = selectedAssociation
        ? Number(p.association_id) === Number(selectedAssociation)
        : true;

      const matchesProd = selectedProduct
        ? Number(p.interet_produit_id) === Number(selectedProduct)
        : true;

      return (
        matchesTerm &&
        matchesStatut &&
        matchesProv &&
        matchesAsso &&
        matchesProd
      );
    });

    out.sort((x, y) => {
      if (sortBy === "nom") {
        return `${x.nom || ""} ${x.prenom || ""}`.localeCompare(
          `${y.nom || ""} ${y.prenom || ""}`,
          "fr"
        );
      }
      if (sortBy === "date") {
        const dx = new Date(x.date_contact || 0).getTime();
        const dy = new Date(y.date_contact || 0).getTime();
        return dy - dx; // récents d'abord
      }
      // statut + nom
      const ix = STATUTS_ORDER.indexOf(x.statut || "");
      const iy = STATUTS_ORDER.indexOf(y.statut || "");
      const cx = ix === -1 ? 999 : ix;
      const cy = iy === -1 ? 999 : iy;
      if (cx !== cy) return cx - cy;
      return (x.nom || "").localeCompare(y.nom || "", "fr");
    });

    return out;
  }, [
    prospects,
    searchTerm,
    selectedStatut,
    selectedProvenance,
    selectedAssociation,
    selectedProduct,
    sortBy,
  ]);

  // KPIs
  const kpis = useMemo(() => {
    const total = filtered.length;
    const by = Object.fromEntries(STATUTS_ORDER.map((s) => [s, 0]));
    let due30 = 0;
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 24 * 3600 * 1000);
    for (const p of filtered) {
      if (by[p.statut] !== undefined) by[p.statut]++;
      const d = p.date_contact ? new Date(p.date_contact) : null;
      if (d && d >= now && d <= in30) due30++;
    }
    return {
      total,
      nouveaux: by["Nouveau Prospect"] || 0,
      enTest: by["En Test"] || 0,
      aRelancer: by["A Recontacter"] || 0,
      echeances30: due30,
    };
  }, [filtered]);

  // CRUD handlers
  const handleAddProspect = async (payload) => {
    try {
      const created = await addProspect(payload);
      if (created) {
        toast.success("Prospect ajouté avec succès !");
        fetchData();
        setShowAddModal(false);
      }
    } catch {
      toast.error("Erreur lors de l'ajout du prospect.");
    }
  };

  const handleUpdateProspect = async (id, patch) => {
    try {
      await updateProspect(id, patch);
      setProspects((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
      );
      toast.success("Prospect mis à jour.");
    } catch {
      toast.error("Erreur lors de la mise à jour du prospect.");
    }
  };

  const handleDeleteProspect = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    try {
      await deleteProspect(id);
      setProspects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Prospect supprimé.");
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedStatut("");
    setSelectedProvenance("");
    setSelectedAssociation("");
    setSelectedProduct("");
    setSortBy("statut");
  };

  const isEmpty = !loading && filtered.length === 0;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className={`rounded-2xl p-6 shadow-sm ${GRADIENT}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Prospects</h1>
            <p className="text-sm opacity-90 mt-1">
              Filtres avancés, édition inline du statut, vue Kanban et modales
              modernes.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              text="Ajouter"
              icon={IoPersonAddSharp}
              onClick={() => setShowAddModal(true)}
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl gap-2"
            />
            <Button
              text="Rafraîchir"
              icon={FaSync}
              onClick={fetchData}
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl gap-2"
            />
            <Button
              text={viewMode === "kanban" ? "Vue Table" : "Vue Kanban"}
              icon={viewMode === "kanban" ? FaTable : FaColumns}
              onClick={() =>
                setViewMode((m) => (m === "kanban" ? "auto" : "kanban"))
              }
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl gap-2"
            />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          <KpiCard label="Total listés" value={kpis.total} />
          <KpiCard label="Nouveaux" value={kpis.nouveaux} />
          <KpiCard label="À recontacter" value={kpis.aRelancer} />
          <KpiCard label="En test" value={kpis.enTest} />
          <KpiCard
            label="RDV < 30j"
            value={kpis.echeances30}
            hint="Dates contact sous 30 jours"
          />
        </div>
      </div>

      {/* Filtres */}
      <div className="rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        <ProspectsFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedStatut={selectedStatut}
          setSelectedStatut={setSelectedStatut}
          selectedProvenance={selectedProvenance}
          setSelectedProvenance={setSelectedProvenance}
          selectedAssociation={selectedAssociation}
          setSelectedAssociation={setSelectedAssociation}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          sortBy={sortBy}
          setSortBy={setSortBy}
          statuts={STATUTS_ORDER}
          provenances={provenanceOptions}
          associations={associationOptions}
          produits={productOptions}
          onReset={resetFilters}
          viewMode={viewMode}
          setViewMode={setViewMode}
          totalCount={filtered.length}
        />
      </div>

      {/* Doublons par email */}
      <DuplicateProspects
        data={prospects}
        filteredData={filtered}
        onEdit={(p) => setEditingProspect(p)}
        onDelete={(id) => handleDeleteProspect(id)}
      />

      {/* Vue principale */}
      <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        {loading ? (
          <SkeletonTable />
        ) : isEmpty ? (
          <EmptyList />
        ) : viewMode === "kanban" ? (
          <KanbanProspectsDnD
            data={filtered}
            statutsOrder={STATUTS_ORDER}
            onMove={(p, newStatut) =>
              handleUpdateProspect(p.id, { statut: newStatut })
            }
            onEdit={(p) => setEditingProspect(p)}
          />
        ) : (
          <>
            {/* AUTO : table ≥ md / cards < md */}
            {viewMode === "auto" && (
              <>
                <div className="hidden md:block">
                  <ProspectsTable
                    data={filtered}
                    onEdit={setEditingProspect}
                    onDelete={(id) => handleDeleteProspect(id)}
                    renderStatut={(p) => (
                      <InlineProspectStatutSelect
                        value={p.statut || ""}
                        onChange={(v) =>
                          handleUpdateProspect(p.id, { statut: v })
                        }
                        options={STATUTS_ORDER}
                      />
                    )}
                  />
                </div>
                <div className="md:hidden">
                  <ProspectsCards
                    data={filtered}
                    onEdit={setEditingProspect}
                    onDelete={(id) => handleDeleteProspect(id)}
                    renderStatut={(p) => (
                      <InlineProspectStatutSelect
                        value={p.statut || ""}
                        onChange={(v) =>
                          handleUpdateProspect(p.id, { statut: v })
                        }
                        options={STATUTS_ORDER}
                        size="sm"
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* FORCÉ TABLE */}
            {viewMode === "table" && (
              <ProspectsTable
                data={filtered}
                onEdit={setEditingProspect}
                onDelete={(id) => handleDeleteProspect(id)}
                renderStatut={(p) => (
                  <InlineProspectStatutSelect
                    value={p.statut || ""}
                    onChange={(v) => handleUpdateProspect(p.id, { statut: v })}
                    options={STATUTS_ORDER}
                  />
                )}
              />
            )}

            {/* FORCÉ CARDS */}
            {viewMode === "cards" && (
              <ProspectsCards
                data={filtered}
                onEdit={setEditingProspect}
                onDelete={(id) => handleDeleteProspect(id)}
                renderStatut={(p) => (
                  <InlineProspectStatutSelect
                    value={p.statut || ""}
                    onChange={(v) => handleUpdateProspect(p.id, { statut: v })}
                    options={STATUTS_ORDER}
                    size="sm"
                  />
                )}
                forceAllBreakpoints
              />
            )}
          </>
        )}
      </div>

      {/* Modales */}
      {showAddModal && (
        <AddProspectModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddProspect}
          associations={associations}
          products={products}
        />
      )}

      {editingProspect && (
        <EditProspectModal
          prospect={editingProspect}
          onClose={() => setEditingProspect(null)}
          onSave={(patch) => handleUpdateProspect(editingProspect.id, patch)}
          associations={associations}
          products={products}
        />
      )}

      <ToastContainer />
    </div>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl p-4 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-slate-900 dark:text-slate-50 tabular-nums">
        {value}
      </div>
      {hint ? (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyList() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        📭
      </div>
      <div className="font-medium">
        Aucun prospect ne correspond à vos filtres.
      </div>
      <div className="text-sm text-slate-500 mt-1">
        Essayez d’élargir votre recherche ou d’ajouter un prospect.
      </div>
    </div>
  );
}
